import {
    RestaurantFilterModel,
    RestaurantTypesManagementModel,
    RestaurantPaymentDetailsModel,
    TaxRequestModel,
    CreateRestaurantGroupModel,
    PagedList,
    RestaurantsGroupModel,
    RestaurantsGroupsListFilter,
    RestaurantsGroupDetailsModel,
    EditRestaurantGroupModel,
    DishStock,
    RestaurantsStock,
    ExportOwnerDishModel,
    RestaurantGroup,
    RestaurantFinance,
    RestaurantPaymentReportDTO,
} from 'api/models';
import {
    RestaurantDetails,
    QRData,
    QRCodesData,
    RestaurantAdvancedSettings,
} from 'store/restaurantDetailsStore/reducer';
import { RestaurantCreateModel, RestaurantPaymentSettings } from 'store/restaurantsStore/reducer';
import agent, { prepareHeaders } from 'api/agent';
import { RestaurantCreateData } from 'components/restaurants/CreateEditRestaurantPage';
import axios, { AxiosResponse } from 'axios';
import { VoucherStatus } from 'components/restaurantInformation/vouchers/components/VoucherStatusBox';

export interface RestaurantCopyModel {
    restaurantModel: RestaurantCreateData;
    workingHours: boolean;
    workAreas: boolean;
    qrCodes: boolean;
    priceLevels: boolean;
    menus: boolean;
    users: boolean;
}

export interface Voucher {
    id: string;
    voucherId: string;
    value: string;
    expirationDate: number;
    useDate: number;
    uploadDate: number;
    status: VoucherStatus;
}

export const Restaurants = {
    Create: (model: RestaurantCreateModel) =>
        agent.requests.put('/Restaurant/create', model, prepareHeaders()),
    Update: (id: string, model: RestaurantCreateModel) =>
        agent.requests.post(`Restaurant/${id}`, model),
    GetList: (
        filterModel: RestaurantFilterModel | null = null,
        page?: number,
        limit?: number,
        asc?: boolean,
        orderBy?: string
    ) =>
        agent.requests.get(
            '/Restaurant/list',
            filterModel != null
                ? {
                      params: {
                          Name: filterModel!.name,
                          AddressLine: filterModel!.addressLine,
                          ZIP: filterModel!.zip,
                          City: filterModel!.city,
                          ShowCoolBoxes: filterModel.showCoolBoxes,
                          page: page,
                          limit: limit,
                          asc: asc,
                          orderBy: orderBy,
                      },
                  }
                : {
                      params: {
                          page: page,
                          limit: limit,
                          asc: asc,
                          orderBy: orderBy,
                      },
                  }
        ),
    GetDetails: (id: string) => agent.requests.get(`Restaurant/${id}`),
    GetRestaurantInformation: () => agent.requests.get('Restaurant/getdetails'),
    UpdateRestaurantInformation: (model: RestaurantDetails) =>
        agent.requests.post('Restaurant/updatedetails', model),
    GetCuisineTypes: () => agent.requests.get('Restaurant/types'),
    CreateCuisineType: (model: { name: string }) =>
        agent.requests.put('Restaurant/types', model, prepareHeaders()),
    ManageCuisineTypes: (model: RestaurantTypesManagementModel) =>
        agent.requests.post('Restaurant/types', model),
    GetQrs: (params = {}) => agent.requests.get('Restaurant/qrcodes', { params }),
    CreateQrCode: (qrCode: QRData, params = {}) =>
        agent.requests.put('Restaurant/qrcodes', qrCode, { ...prepareHeaders(), params }),
    UpdateQrCode: (qrCode: QRData) => agent.requests.post('Restaurant/qrcodes', qrCode),
    DeleteQrCode: (qrIds: string[]) => agent.requests.delete('Restaurant/qrcodes', qrIds),
    GetFinance: (dateFrom: any, dateTo: any, restaurantId = ''): Promise<RestaurantFinance> =>
        agent.requests.get('Restaurant/finance', {
            params: {
                dateFrom,
                dateTo,
                restaurantId,
            },
        }),
    GetFinanceExcel: (dateFrom: string, dateTo: string, restaurantId?: string) =>
        axios.get('Restaurant/finance/export-excel', {
            ...prepareHeaders(true),
            params: {
                dateFrom,
                dateTo,
                restaurantId,
            },
            responseType: 'blob',
        }),
    GetTipsData: (dateFrom: any, dateTo: any, restaurantId = '') =>
        agent.requests.get('Restaurant/finance/tips', {
            params: {
                dateFrom,
                dateTo,
                restaurantId,
            },
        }),
    GetStornoData: (dateFrom: any, dateTo: any, restaurantId = '') =>
        agent.requests.get('Restaurant/finance/storno', {
            params: {
                dateFrom,
                dateTo,
                restaurantId,
            },
        }),
    DisableRestaurant: (id: string) => agent.requests.post(`Restaurant/disable/${id}`),
    UpdatePaymentDetails: (model: RestaurantPaymentDetailsModel) =>
        agent.requests.post('Restaurant/paymentdetails', model),
    UpdateQrCodesNames: (model: QRCodesData) =>
        agent.requests.post('Restaurant/qrcodes/name', model),
    GetTaxes: () => agent.requests.get('Restaurant/taxes'),
    UpdateTaxes: (id: string, model: TaxRequestModel) =>
        agent.requests.post(`Restaurant/taxes/${id}`, model),
    CreatePricingLevel: (restaurantId: string, name: string) =>
        agent.requests.post(`Restaurant/${restaurantId}/pricinglevel`, { name }),
    UpdatePricingLevel: (restaurantId: string, id: string, name: string) =>
        agent.requests.put(
            `Restaurant/${restaurantId}/pricinglevel/${id}`,
            { name },
            prepareHeaders()
        ),
    DeletePricingLevel: (restaurantId: string, id: string) =>
        agent.requests.delete(`Restaurant/${restaurantId}/pricinglevel/${id}`),
    GetSimpleList: () => agent.requests.get('/Restaurant/simplelist'),
    CopyRestaurant: (restaurantId: string, model: RestaurantCopyModel) =>
        agent.requests.post(`Restaurant/${restaurantId}/copy`, model),
    GetRestaurantsGroups: (
        params: RestaurantsGroupsListFilter
    ): Promise<PagedList<RestaurantsGroupModel>> => {
        const searchParams: { [key: string]: any } = {};
        if (params.name) searchParams.name = params.name;
        if (params.address) searchParams.address = params.address;
        if (params.zip) searchParams.zip = params.zip;
        if (params.city) searchParams.city = params.city;
        searchParams.showCoolBoxes = !!params.showCoolBoxes;
        searchParams.page = params.page !== undefined ? params.page : 0;
        searchParams.limit = params.limit || 10;
        return agent.requests.get('Restaurant/group', {
            params: searchParams,
        });
    },
    GetRestaurantGroup: (id: string): Promise<RestaurantsGroupDetailsModel> =>
        agent.requests.get(`Restaurant/group/${id}`),
    CreateRestaurantGroup: (model: CreateRestaurantGroupModel): Promise<RestaurantsGroupModel> =>
        agent.requests.post('Restaurant/group', model),
    UpdateRestaurantGroup: (
        id: string,
        model: EditRestaurantGroupModel
    ): Promise<RestaurantsGroupModel> =>
        agent.requests.put(`Restaurant/group/${id}`, model, prepareHeaders()),
    DisableRestaurantGroup: (id: string): Promise<RestaurantsGroupModel> =>
        agent.requests.post(`Restaurant/group/${id}/enabledisable`),
    DeleteRestaurantGroup: (id: string): Promise<string> =>
        agent.requests.delete(`Restaurant/group/${id}`),
    SetPaymentSettings: (id: string, model: RestaurantPaymentSettings) =>
        agent.requests.post(`Restaurant/${id}/payment-settings`, model),
    UpdateRestaurantAdvancedSettings: (model: RestaurantAdvancedSettings) =>
        agent.requests.post('Restaurant/advanced-settings', model),
    GetStornoReasons: () => agent.requests.get('Restaurant/storno-reasons'),
    GetStock: (from: string, to: string): Promise<DishStock[]> =>
        agent.requests.get('Restaurant/stock', { params: { from, to } }),
    GetOwnerRestaurantsStock: (from: string, to: string): Promise<RestaurantsStock> =>
        agent.requests.get('Restaurant/stock/owner', { params: { from, to } }),
    GetVouchers: (
        page: number = 0,
        limit: number = 0,
        status: string | null = null,
        dateFrom: number = 0,
        dateTo: number = 0
    ): Promise<PagedList<Voucher>> =>
        agent.requests.get('Restaurant/vouchers', {
            params: {
                page,
                limit,
                status,
                dateFrom,
                dateTo,
            },
        }),
    ImportVouchers: (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append('file', file);
        return agent.requests.post('Restaurant/vouchers/import', formData);
    },
    GetVouchersTemplate: () =>
        axios.get('Restaurant/vouchers/template', { ...prepareHeaders(), responseType: 'blob' }),
    ExportStock: (from: string, to: string, dishIds: string[]): Promise<AxiosResponse<Blob>> => {
        return axios.post('Restaurant/stock-export', dishIds, {
            ...prepareHeaders(),
            params: { from, to },
            responseType: 'blob',
        });
    },
    ExportOwnerStock: (
        from: string,
        to: string,
        groupName: string,
        dishes: ExportOwnerDishModel[]
    ): Promise<AxiosResponse<Blob>> => {
        return axios.post(
            'Restaurant/owner/stock-export',
            {
                groupName,
                dishes,
            },
            {
                ...prepareHeaders(),
                params: { from, to },
                responseType: 'blob',
            }
        );
    },
    GetMyGroups: (): Promise<RestaurantGroup[]> => {
        return agent.requests.get('Restaurant/my-groups');
    },
    GetPaymentsReport: (
        from: Date,
        to: Date,
        restaurantId?: string
    ): Promise<RestaurantPaymentReportDTO> => {
        var params = new URLSearchParams({
            dateFrom: from.toISOString(),
            dateTo: to.toISOString(),
        });

        if (restaurantId) {
            params.append('restaurantId', restaurantId);
        }
        return agent.requests.get('Restaurant/finance-payments', { params });
    },
};
